/**
 * NotesPanel — col-2 "Notes & Snippets".
 *
 * Notes are snippets only (mapped from Assessment Prep / Scribe via
 * /source-snippets). Provides: Show All Notes toggle, search, four
 * source-type filter chips, note cards with delete + Show more,
 * Delete-all (active section), and the jurisdiction ChoicesEditor for the
 * active section. In the All-Notes view (no filter active) cards are
 * drag-reorderable; the order persists via onReorder (MDRS-8).
 */
import { useEffect, useMemo, useState } from "react";

import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronLeft,
  GripVertical,
  Info,
  Search,
  Trash2,
} from "lucide-react";
import { useDispatch } from "react-redux";

import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { Switch } from "@components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { useDeleteModuleExtract } from "@hooks/mutations/use-module-extracts";

import { setMessage } from "@redux/notification";

import { stripBundlePrefix } from "@utils/bundle-name";
import { cn } from "@utils/ui";

import type { NoteFilters, NoteSource, ReportNote } from "../editor/notes";
import {
  EMPTY_FILTERS,
  NOTE_SOURCES,
  SOURCE_LABEL,
  filterNotes,
  sortNotesForAllView,
} from "../editor/notes";
import type { ModuleChoice } from "../editor/utils";
import { ChoicesEditor } from "./ChoicesEditor";

type Props = {
  activeNotes: ReportNote[];
  allNotes: ReportNote[];
  activeSection: string | null;
  /**
   * "Show All Notes" is owned by the editor context (it deselects the active
   * section), so it's passed in rather than held here. Off = active section
   * only; on = every section's notes.
   */
  showAll: boolean;
  onShowAllChange: (on: boolean) => void;
  activeChoices: ModuleChoice[] | null;
  choiceByModule: Record<string, string>;
  onChoiceChange: (moduleName: string, choiceKey: string) => void;
  /** slug → human label for the scribe field provenance footer. */
  fieldLabel: (slug: string) => string;
  /**
   * Persist a new All-Notes order — receives the full ordered list of note
   * ids after a drag. Only wired in the All-Notes view; when absent (or a
   * filter is active) cards are not draggable. Should reject if the save
   * fails so the optimistic order can be rolled back.
   */
  onReorder?: (orderedIds: string[]) => Promise<void>;
  onCollapse?: () => void;
};

export const NotesPanel = ({
  activeNotes,
  allNotes,
  activeSection,
  showAll,
  onShowAllChange,
  activeChoices,
  choiceByModule,
  onChoiceChange,
  fieldLabel,
  onReorder,
  onCollapse,
}: Props) => {
  const [filters, setFilters] = useState<NoteFilters>(EMPTY_FILTERS);
  // Pending confirmation: a single note id, or "all" (active section).
  const [pendingDelete, setPendingDelete] = useState<string | "all" | null>(null);
  // Optimistic All-Notes order applied immediately on drop, before the
  // persisted global_order round-trips back via /source-snippets. Reconciled
  // (cleared) once a fresh allNotes set arrives — see the effect below.
  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);

  const dispatch = useDispatch();
  const deleteExtract = useDeleteModuleExtract();

  const filterActive = filters.sources.length > 0 || filters.search.trim() !== "";

  // The server's persisted All-Notes order (by global_order).
  const serverOrderKey = useMemo(
    () =>
      sortNotesForAllView(allNotes)
        .map((n) => n.id)
        .join(","),
    [allNotes]
  );
  // Reconcile: drop the optimistic override once the refetched server order
  // matches what we applied (the reorder persisted), or the note *set* changed
  // underneath us (add/delete) — in both cases the server is authoritative.
  useEffect(() => {
    if (!optimisticIds) return;
    const serverIds = serverOrderKey ? serverOrderKey.split(",") : [];
    const sameSet =
      serverIds.length === optimisticIds.length &&
      serverIds.every((id) => optimisticIds.includes(id));
    if (!sameSet || optimisticIds.join(",") === serverOrderKey) {
      setOptimisticIds(null);
    }
  }, [optimisticIds, serverOrderKey]);

  // All-Notes is shown in the manual (global_order) sort; the section view
  // keeps the backend order. While an optimistic drag order is pending, honour
  // it over the server sort so the card stays where the user dropped it.
  const source = useMemo(() => {
    if (!showAll) return activeNotes;
    const sorted = sortNotesForAllView(allNotes);
    if (!optimisticIds) return sorted;
    const byId = new Map(sorted.map((n) => [n.id, n]));
    const reordered = optimisticIds
      .map((id) => byId.get(id))
      .filter((n): n is (typeof sorted)[number] => !!n);
    // Append any notes not covered by the optimistic list (defensive).
    for (const n of sorted) if (!optimisticIds.includes(n.id)) reordered.push(n);
    return reordered;
  }, [showAll, activeNotes, allNotes, optimisticIds]);
  const notes = useMemo(() => filterNotes(source, filters), [source, filters]);

  // Drag-reorder is only meaningful for the full, unfiltered All-Notes list —
  // reordering a filtered subset has no well-defined persisted meaning, so we
  // disable it whenever a chip/search filter is active (MDRS-8 huddle 6/11).
  const canDrag = showAll && !filterActive && !!onReorder;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Id of the card currently being dragged — drives the DragOverlay clone.
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeNote = activeId ? notes.find((n) => n.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const ids = notes.map((n) => n.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const next = [...ids];
    next.splice(to, 0, next.splice(from, 1)[0]);
    // Apply locally first so the card stays put, then persist. If the save
    // fails, roll the optimistic order back so the UI reflects what's actually
    // stored (the reconcile effect only clears on success or a set change).
    setOptimisticIds(next);
    void onReorder(next).catch(() => setOptimisticIds(null));
  };

  const toggleSource = (s: NoteSource) =>
    setFilters((f) => ({
      ...f,
      sources: f.sources.includes(s)
        ? f.sources.filter((x) => x !== s)
        : [...f.sources, s],
    }));

  const confirmDeleteAll = pendingDelete === "all";
  const confirmCount = confirmDeleteAll ? activeNotes.length : 1;

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const ids =
      pendingDelete === "all" ? activeNotes.map((n) => n.id) : [pendingDelete];
    try {
      await Promise.all(ids.map((id) => deleteExtract.mutateAsync(id)));
      dispatch(
        setMessage({
          type: "success",
          content:
            ids.length === 1 ? "Note deleted." : `Deleted ${ids.length} notes.`,
        })
      );
    } catch (error) {
      dispatch(
        setMessage({
          type: "error",
          content:
            error instanceof Error ? error.message : "Failed to delete note(s).",
        })
      );
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <aside className="flex h-full min-h-0 w-full flex-col bg-background-white">
      <div className="flex items-center justify-between px-4 pb-3 pt-4">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold">Notes &amp; Snippets</h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="About Notes &amp; Snippets"
                className="text-foreground-muted hover:text-foreground"
              >
                <Info className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px]">
              Notes and snippets captured from Assessment Prep and Assessment Scribe
              during document review and transcription.
            </TooltipContent>
          </Tooltip>
        </div>
        {onCollapse ? (
          <button
            type="button"
            onClick={onCollapse}
            className="text-foreground-muted hover:text-foreground"
            title="Hide notes"
          >
            <ChevronLeft className="size-4" />
          </button>
        ) : null}
      </div>

      <div className="px-4 pt-3">
        <label className="flex cursor-pointer items-center justify-between gap-2">
          <span className="text-sm font-medium">Show All Notes</span>
          <Switch checked={showAll} onCheckedChange={onShowAllChange} />
        </label>
      </div>

      <div className="px-4 pt-3">
        <Input
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          placeholder="Search notes or snippets"
          leftIcon={<Search className="size-3.5" />}
          className="h-9 text-sm"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 px-4 pb-1 pt-3">
        <span className="mr-1 text-xs text-foreground-muted">Filter by:</span>
        {NOTE_SOURCES.map((s) => (
          <Chip
            key={s}
            active={filters.sources.includes(s)}
            onClick={() => toggleSource(s)}
          >
            {SOURCE_LABEL[s]}
          </Chip>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3 pt-2">
        {!showAll && !activeSection ? (
          // All-Notes was toggled off without a section selected — prompt the
          // user to pick one again (MDRS-8).
          <div
            data-testid="no-section-placeholder"
            className="rounded-md border border-dashed bg-[#FAFAFA] p-6 text-center text-xs text-foreground-muted"
          >
            Select a section to view its notes, or turn on “Show All Notes”.
          </div>
        ) : (
          <>
            {!showAll && activeChoices && activeSection ? (
              <ChoicesEditor
                activeSection={activeSection}
                choices={activeChoices}
                choiceByModule={choiceByModule}
                onChoiceChange={onChoiceChange}
              />
            ) : null}

            {notes.length === 0 ? (
              <div className="rounded-md border border-dashed bg-[#FAFAFA] p-6 text-center text-xs text-foreground-muted">
                No notes yet. Map snippets or highlights from Assessment Prep or
                Scribe to see them here.
              </div>
            ) : canDrag ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveId(null)}
              >
                <SortableContext
                  items={notes.map((n) => n.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <SortableNoteCard
                        key={note.id}
                        note={note}
                        showSection={showAll}
                        fieldLabel={fieldLabel}
                        onDelete={() => setPendingDelete(note.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
                {/* Floating clone that follows the cursor while dragging. */}
                <DragOverlay>
                  {activeNote ? (
                    <NoteCard
                      note={activeNote}
                      showSection={showAll}
                      fieldLabel={fieldLabel}
                      onDelete={() => {}}
                      isOverlay
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    showSection={showAll}
                    fieldLabel={fieldLabel}
                    onDelete={() => setPendingDelete(note.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end border-t px-4 py-2.5">
        <button
          type="button"
          onClick={() => setPendingDelete("all")}
          disabled={activeNotes.length === 0 || deleteExtract.isPending}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive hover:underline disabled:cursor-not-allowed disabled:text-foreground-muted disabled:no-underline"
        >
          <Trash2 className="size-3.5" />
          Delete all notes
        </button>
      </div>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <DialogContent className="max-w-md rounded-2xl bg-background px-8 py-10">
          <DialogHeader className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <Trash2 className="size-6 text-destructive" />
            </div>
            <DialogTitle>
              {confirmDeleteAll ? "Delete all notes" : "Delete note"}
            </DialogTitle>
          </DialogHeader>
          <p className="mt-4 text-center text-sm text-foreground-muted">
            {confirmDeleteAll
              ? `Delete all ${confirmCount} note${confirmCount === 1 ? "" : "s"} in this section? This can't be undone.`
              : "Delete this note? This can't be undone."}
          </p>
          <div className="mt-6 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setPendingDelete(null)}
              disabled={deleteExtract.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleConfirmDelete}
              disabled={deleteExtract.isPending}
            >
              {deleteExtract.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
};

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
      active
        ? "bg-primary text-primary-foreground"
        : "bg-primary-50 text-primary hover:bg-primary-100"
    )}
  >
    {children}
  </button>
);

type NoteCardProps = {
  note: ReportNote;
  showSection: boolean;
  fieldLabel: (slug: string) => string;
  onDelete: () => void;
};

/**
 * Sortable wrapper for the All-Notes drag view. Adds a grip handle (drag is
 * handle-only so Delete/Show-more stay clickable) and applies the
 * transform from @dnd-kit. While dragging, the original collapses to a dashed
 * placeholder gap and the DragOverlay renders the floating clone.
 */
const SortableNoteCard = (props: NoteCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.note.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <NoteCard
        {...props}
        isPlaceholder={isDragging}
        dragHandle={
          <button
            ref={setActivatorNodeRef}
            type="button"
            aria-label="Drag to reorder"
            className={cn(
              "flex size-3 shrink-0 cursor-grab items-center justify-center rounded text-foreground-muted/50 hover:text-foreground active:cursor-grabbing"
            )}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-3" />
          </button>
        }
      />
    </div>
  );
};

const NoteCard = ({
  note,
  showSection,
  fieldLabel,
  onDelete,
  dragHandle,
  isPlaceholder,
  isOverlay,
}: NoteCardProps & {
  dragHandle?: React.ReactNode;
  /** The original card while its clone is dragging — dim to a drop slot. */
  isPlaceholder?: boolean;
  /** The floating DragOverlay clone — lifted with a shadow. */
  isOverlay?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = note.text.length > 180;

  const isScribe = note.source === "scribe_ai" || note.source === "scribe_text";
  // Provenance detail after the source label: Scribe → assessment-field label
  // (e.g. "History of Injury"); Prep → source file + page (e.g. "LOI.pdf · p.2").
  const detail = isScribe
    ? note.categorySlug
      ? fieldLabel(note.categorySlug)
      : "Summary"
    : [
        note.sourceFileName ? stripBundlePrefix(note.sourceFileName) : null,
        note.pageNumber ? `p.${note.pageNumber}` : null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <div
      className={cn(
        "group/note flex gap-1 rounded-xl border border-border bg-background-white p-2.5 transition-all",
        // Subtle interactive affordance on hover (signals draggability).
        !isPlaceholder && !isOverlay && "hover:border-primary/30 hover:shadow-sm",
        // The floating clone: lift it off the page.
        isOverlay && "cursor-grabbing shadow-xl ring-2 ring-primary/30",
        // The original under a dragging clone: fade to a dashed drop slot.
        isPlaceholder && "border-dashed bg-primary/5 opacity-50 [&_*]:invisible"
      )}
    >
      {/* In-flow handle column — always reserves its width (the handle only
          toggles opacity on hover/focus), so revealing it never shifts the
          card text. */}
      {dragHandle ? (
        // display:none until hover/keyboard focus, then a centred flex column.
        // (A plain `block` can't vertically centre its child — flex/grid is
        // required for that, so we toggle hidden ↔ flex.)
        <div className="hidden w-3 items-center justify-center focus-within:flex group-hover/note:flex">
          {dragHandle}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "whitespace-pre-line text-sm leading-relaxed !text-foreground [text-decoration:none]",
            !expanded && isLong && "line-clamp-3"
          )}
        >
          {note.text}
        </p>
        {isLong ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-primary underline"
          >
            {expanded ? "Show less" : "Show more"}
            <ChevronDown
              className={cn("size-4 transition-transform", expanded && "rotate-180")}
            />
          </button>
        ) : null}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-sm text-foreground-muted">
            {showSection ? `${note.sectionLabel} · ` : ""}
            {SOURCE_LABEL[note.source]}
            {detail ? `: ${detail}` : ""}
          </span>
          <div className="-mr-1 flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={onDelete}
              title="Delete note"
              className="flex size-6 items-center justify-center rounded text-destructive hover:bg-destructive-50"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
